import React, { useEffect, useState } from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import api from '../../utils/api';
import { SUPPORT_TEAM } from '../../utils/supportTeam';
import { DEFAULT_ACCOUNT_MANAGER } from '../../utils/defaultAccountManager';

/**
 * Displays the HealthCues support team as it appears in the employer portal.
 *
 * The three onboarding specialists (Angie Comer / Cassie Nix / Kate Hawn) are
 * the canonical support team — pulled from `/utils/supportTeam.js` so the
 * employer portal, internal CRM, and messaging system always agree on names,
 * roles, and contact info. Per-client overrides via
 * `client.onboarding.team.{contracts|payroll|enrollment}` are still respected
 * for tenants that want a custom roster.
 *
 * The assigned Account Manager (resolved from `client.crm.assignedTo`) appears
 * underneath as the dedicated relationship owner. If no AM is assigned, we
 * fall back to the system default (Ryan Young).
 */

export default function YourHealthCuesTeamSection({ client }) {
  const [accountManager, setAccountManager] = useState(null);
  const [defaultAm, setDefaultAm] = useState(null);

  const assignedTo = client?.crm?.assignedTo;
  const onboardingTeam = client?.onboarding?.team || {};
  const clientId = client?.id || client?._id;

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    // Single endpoint returns the assigned AM (or Ryan Young as fallback).
    // Works for super users, account managers, AND employer contacts (their
    // own client). Replaces the old `/api/users` lookup which 403'd for
    // employer-portal users \u2014 that bug is what caused "Loading your account
    // manager\u2026" to display indefinitely.
    (async () => {
      try {
        const am = await api.dashboard.assignedAccountManager(clientId);
        if (!cancelled) {
          if (am?.isDefault) {
            setDefaultAm(am);
            setAccountManager(null);
          } else {
            setAccountManager(am);
            setDefaultAm(null);
          }
        }
      } catch (err) {
        if (!cancelled) setDefaultAm(DEFAULT_ACCOUNT_MANAGER);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, assignedTo]);

  // Per-client overrides — admin team can map specific people for each role.
  const overridden = [
    onboardingTeam.contracts && onboardingTeam.contracts.name
      ? { id: 'contracts', name: onboardingTeam.contracts.name, role: onboardingTeam.contracts.title || 'Contracts', color: C.accent, email: onboardingTeam.contracts.email, phone: onboardingTeam.contracts.phone }
      : null,
    onboardingTeam.payroll && onboardingTeam.payroll.name
      ? { id: 'payroll', name: onboardingTeam.payroll.name, role: onboardingTeam.payroll.title || 'Payroll', color: C.green, email: onboardingTeam.payroll.email, phone: onboardingTeam.payroll.phone }
      : null,
    onboardingTeam.enrollment && onboardingTeam.enrollment.name
      ? { id: 'enrollment', name: onboardingTeam.enrollment.name, role: onboardingTeam.enrollment.title || 'Enrollment', color: C.purple, email: onboardingTeam.enrollment.email, phone: onboardingTeam.enrollment.phone }
      : null,
  ].filter(Boolean);

  // Canonical support team (authoritative names from supportTeam.js)
  const supportTeam = overridden.length > 0
    ? overridden
    : SUPPORT_TEAM.map((m) => ({
        id: m.id,
        name: m.name,
        role: m.role,
        color: m.color,
        email: m.email,
        phone: m.phone,
      }));

  return (
    <div data-testid="team-section" style={{ ...S.card, padding: 24 }}>
      <SectionHeader title="Your HealthCues Team" icon="people-circle-outline" />

      {/* Account Manager — appears FIRST as the dedicated relationship owner */}
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: C.textLight,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        Account Manager
      </div>
      {accountManager ? (
        <AccountManagerCard user={accountManager} />
      ) : defaultAm ? (
        <AccountManagerCard user={defaultAm} isDefault />
      ) : (
        <div style={{ fontSize: 12, color: C.textLight, fontStyle: 'italic' }}>
          Loading your account manager…
        </div>
      )}

      {/* Onboarding Specialists — appear UNDER the AM */}
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: C.textLight,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginTop: 22,
          marginBottom: 10,
        }}
      >
        Onboarding Specialists
      </div>
      <div
        data-testid="support-team-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 10,
        }}
      >
        {supportTeam.map((member) => (
          <SpecialistCard key={member.id} member={member} />
        ))}
      </div>
    </div>
  );
}

function SectionHeader({ title, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <Icon name={icon} style={{ fontSize: 20, color: C.accent }} />
      <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>{title}</h2>
    </div>
  );
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function roleToTitle(role) {
  const map = {
    super_user: 'HealthCues Team',
    account_manager: 'Account Manager',
    employer_contact: 'Employer Contact',
    employee: 'Employee',
  };
  return map[role] || 'HealthCues Team';
}

function AccountManagerCard({ user, isDefault = false }) {
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Account Manager';
  const title = user.title || roleToTitle(user.role);
  return (
    <div
      data-testid="account-manager-card"
      style={{
        background: '#0c2b54',
        color: '#fff',
        borderRadius: 10,
        padding: 18,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: C.accent,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {initials(name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 800 }}>{name}</div>
        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>{title}</div>
        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {user.email && (
            <span>
              <Icon name="mail-outline" style={{ fontSize: 12, marginRight: 4 }} />
              {user.email}
            </span>
          )}
          {user.phone && (
            <span>
              <Icon name="call-outline" style={{ fontSize: 12, marginRight: 4 }} />
              {user.phone}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SpecialistCard({ member }) {
  return (
    <div
      data-testid={`support-card-${member.id}`}
      style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          background: member.color || C.accent,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {initials(member.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{member.name}</div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: member.color || C.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginTop: 1,
          }}
        >
          {member.role}
        </div>
        {member.phone && <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>{member.phone}</div>}
        {member.email && <div style={{ fontSize: 11, color: C.textLight, marginTop: 1 }}>{member.email}</div>}
      </div>
    </div>
  );
}
